/* eslint-disable consistent-return */
import { bootstrap, ContentBlockRenderer } from '@plugins/next-cms-core';

import DefaultTemplate from '@components/templates/DefaultTemplate';
import Error from 'next/error';
import Head from 'next/head';
import PageInsetTop from '@components/atoms/PageInsetTop';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { Box } from '@mui/material';
import Banner from '@components/molecules/Banner';
import { getAppData } from '@lib/appInitialState';
import { components } from '../../cmsconfig';

// eslint-disable-next-line no-unused-vars
export default function BlogItem({
  appData,
  page,
}) {
  let isBannerRendered = false;

  if (!page) {
    return (
      <Error statusCode={404} title="Blogartikel nicht gefunden" />
    );
  }

  if (!page.publishedAt) {
    return (
      <Error statusCode={410} title="Blogartikel ist nicht mehr vorhanden" />
    );
  }

  const {
    metaPageTitle,
    metaPageDescription,
    isTopContentOffset,
  } = page;
  const componentData = {
    id: page.id,
    blocks: page.content ?? [],
  };

  const pageTitle = `${metaPageTitle ?? ''}`;

  return (
    <>
      <Head>
        <title>
          {pageTitle}
        </title>
        <meta content={metaPageDescription} name="description" />
        <meta content={pageTitle} property="og:title" />
      </Head>
      <DefaultTemplate>
        {isTopContentOffset && <PageInsetTop />}
        <ContentBlockRenderer
          components={components}
          context={{ page }}
          data={componentData}
          appData={appData}
          // eslint-disable-next-line react/no-unstable-nested-components
          preComponentRender={(component) => {
            // eslint-disable-next-line no-underscore-dangle
            if (!isBannerRendered && !PRE_RENDER_SKIP_COMPONENTS.includes(component.typeName)) {
              isBannerRendered = true;

              return (
                <Box my={3}>
                  <Banner />
                </Box>
              );
            }
          }}
        />
      </DefaultTemplate>
    </>
  );
}

BlogItem.propTypes = {
  page: PropTypes.object,
};

export async function getServerSideProps(ctx) {
  const {
    query,
    res,
    locale,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);
  const { slug } = query;

  const {
    data,
    error,
  } = await graphQlClient.query({
    query: FETCH_PAGE,
    variables: {
      slug,
      locale,
      localeStr: locale,
    },
  });

  if (error) {
    throw error;
  }

  if (!data.resolveBlogPageBySlug && res) {
    res.statusCode = 404;
  } else if (!data.resolveBlogPageBySlug?.publishedAt && res) {
    res.statusCode = 410;
  }

  if (res && data.cacheState.isEnabled) {
    res.setHeader('Cache-Control', 'public, max-age=3600');
  }

  const statusCode = res?.statusCode ?? 200;

  return {
    props: {
      ...appData,
      statusCode: res?.statusCode ?? 200,
      slug,
      page: data.resolveBlogPageBySlug,
      pageBottomConfig: {
        bottomInfoText: data.resolveBlogPageBySlug?.bottomInfoText ?? null,
      },
      isGlobalNoticeHidden: Boolean(data.resolveBlogPageBySlug?.isGlobalNoticeHidden),
      canonicalUrl: data.resolveBlogPageBySlug?.metaCanonicalUrl ?? null,
    },
  };
}

const { graphQlFragments } = bootstrap({
  components,
});

const PRE_RENDER_SKIP_COMPONENTS = [
  'ComponentContentPageHeader',
  'ComponentContentCarSearchFilter',
  'ComponentContentModelRequestForm',
];

const FETCH_PAGE = gql`
  query GetPageBySlug($slug: String!, $locale: I18NLocaleCode!, $localeStr: String!) {
    resolveBlogPageBySlug(locale: $locale, slug: $slug) {
      publishedAt
      publishDate
      name
      isTopContentOffset
      bottomInfoText
      metaPageTitle
      metaPageDescription
      metaCanonicalUrl
      content {
        ${graphQlFragments}
      }
    }
    cacheState {
      isEnabled
    }
  }
`;
